import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { Avatar, Card, EmptyState, ErrorState, Skeleton } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';

export interface ConversationSummary {
  id: string;
  type?: string;
  counterpart: Array<{ id: string; name: string; role: string; photoUrl: string | null }>;
  request: { id: string; status: string; originAddress: string; destAddress: string; scheduledAt: string | null; serviceType: { name: string }; rides: Array<{ id: string; code: string }> } | null;
  lastMessage: { body: string | null; at: string; mine: boolean } | null;
  unread: number;
}

export const useChatUnread = () => useQuery({ queryKey: ['conversations', 'unread'], queryFn: () => api<{ count: number }>('/conversations/unread-count'), refetchInterval: 60_000 });

export function ChatListPage({ base }: { base: '/app' | '/driver' }) {
  const q = useQuery({ queryKey: ['conversations'], queryFn: () => api<ConversationSummary[]>('/conversations') });
  return (
    <Page title="Chat" onRefresh={() => q.refetch()}>
      {q.isLoading && <Skeleton className="h-24" />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {q.data?.length === 0 && <EmptyState icon={<MessageCircle className="size-6" />} title="Sin conversaciones" description="El chat se abre automáticamente con cada solicitud de viaje." />}
      <div className="flex flex-col gap-2">
        {q.data?.map((c) => {
          const other = c.counterpart[0];
          return (
            <Link key={c.id} to={`${base}/chat/${c.id}`}>
              <Card className="p-3 flex items-center gap-3">
                <Avatar src={other?.photoUrl} firstName={other?.name.split(' ')[0]} lastName={other?.name.split(' ')[1]} size={48} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold truncate">{c.type === 'SUPPORT' ? (c.counterpart.every((x) => x.role === 'TENANT_ADMIN') ? 'Soporte' : `Soporte · ${c.counterpart.filter((x) => x.role !== 'TENANT_ADMIN').map((x) => x.name).join(', ')}`) : c.counterpart.map((x) => x.name).join(', ') || 'Conversación'}</p>
                    {c.lastMessage && <span className="text-xs text-[var(--text-muted)] shrink-0">{fmtDateTime(c.lastMessage.at)}</span>}
                  </div>
                  <p className="text-xs text-[var(--text-muted)] truncate">{c.type === 'SUPPORT' ? 'Chat con la administración' : c.request ? `${c.request.serviceType.name}${c.request.rides[0] ? ` · ${c.request.rides[0].code}` : ''}` : ''}</p>
                  <p className={`text-sm truncate ${c.unread ? 'font-semibold' : 'text-[var(--text-muted)]'}`}>{c.lastMessage ? (<>{c.lastMessage.mine ? 'Tú: ' : ''}<span data-no-translate>{c.lastMessage.body ?? ''}</span></>) : 'Sin mensajes todavía'}</p>
                </div>
                {c.unread > 0 && <span className="min-w-6 h-6 px-1.5 rounded-full bg-brand-red text-white text-xs font-bold flex items-center justify-center">{c.unread}</span>}
              </Card>
            </Link>
          );
        })}
      </div>
    </Page>
  );
}
